import { Filter, IDateRangeWithCompareFilter, IFilters, RatingFilter } from "constants/filters";
import { IPriorityFilter } from "constants/filters/priority-filter";
import { ReportDistribution } from "constants/reports/distribution";
import { ReportType } from "constants/reports/report-type";
import { IKeyValue } from "helpers/url";
import { IKeyAny } from "services/api/api-client";
import {
  IFirstResponseTimePerEntity,
  IHandlingTimeByStatusPerEntity,
  ISolvedAndClosedStats,
  ITicketRatingsPerEntity,
  ITicketResolutionTimePerEntity,
  ITicketsStatusStats,
} from "services/api/report/interfaces";
import { KeyMap } from "utils/interface";

export interface IReportsViewCacheExpirationTime {
  [key: string]: Date;
}

export interface IReportsViewGroupFilter {
  groups: IFilters[Filter.Group];
  compare?: IFilters[Filter.Group];
}

export interface IReportsViewAgentFilter {
  agents: IFilters[Filter.Agent];
  compare?: IFilters[Filter.Agent];
}

export interface IReportsViewFilters {
  [Filter.Agent]?: IReportsViewAgentFilter;
  [Filter.DateRange]?: IDateRangeWithCompareFilter;
  [Filter.Group]?: IReportsViewGroupFilter;
  [Filter.Tag]?: IFilters[Filter.Tag];
  [Filter.Priority]?: IFilters[Filter.Priority];
  [Filter.DetailedSource]?: IFilters[Filter.DetailedSource];
  [Filter.Action]?: IFilters[Filter.Action];
  [Filter.Target]?: IFilters[Filter.Target];
  [Filter.Author]?: IFilters[Filter.Author];
  [Filter.PageSize]?: IFilters[Filter.PageSize];
  [Filter.NextId]?: IFilters[Filter.NextId];
  [Filter.PrevId]?: IFilters[Filter.PrevId];
  [Filter.NextValue]?: IFilters[Filter.NextValue];
  [Filter.PrevValue]?: IFilters[Filter.PrevValue];
  [Filter.Language]?: IFilters[Filter.Language];
}

export interface IReportsViewParams {
  distribution?: ReportDistribution;
  dateFrom?: string | Date;
  dateTo?: string | Date;
  weekday?: number;
  [Filter.Agent]?: IReportsViewAgentFilter;
  [Filter.Group]?: IReportsViewGroupFilter;
  [Filter.Rating]?: RatingFilter[];
  [Filter.Tag]?: string[];
  [Filter.DateRange]?: IDateRangeWithCompareFilter;
  [Filter.Priority]?: IPriorityFilter;
  [Filter.DetailedSource]?: string[];
  [Filter.Action]?: string;
  [Filter.Target]?: string;
  [Filter.Author]?: string;
  [Filter.PageSize]?: number;
  [Filter.NextId]?: string;
  [Filter.PrevId]?: string;
  [Filter.NextValue]?: string;
  [Filter.PrevValue]?: string;
  [Filter.Language]?: string[];
}

export interface IReportsViewData {
  [ReportType.Last7days]?: ILast7DaysReportData;
  [ReportType.TicketRatings]?: ITicketRatingsData;
  [ReportType.ScheduledReports]?: IScheduledReportsData;
  [ReportType.SolvedAndClosed]?: ISolvedClosedTicketsData;
  [ReportType.TicketFirstResponseTime]?: ITicketFirstResponseTimeData;
  [ReportType.NewTickets]?: INewTicketsData;
  [ReportType.NewTicketsHeatmap]?: IHeatmapData;
  [ReportType.TicketResolutionTime]?: ITicketResolutionTimeData;
  [ReportType.HandlingTimeByStatus]?: IHandlingTimeByStatusData;
}

export interface IFetchDataPayload {
  reportType: ReportType;
}

export interface IChartSummary {
  name?: string;
  value: string | number | React.ReactNode;
  label?: string | number | React.ReactNode;
  color?: string;
  urlParams?: IKeyAny;
  disableLink?: boolean;
  filters?: IKeyValue<string>;
  rawValue?: number;
}

export interface IChartData {
  summary: IChartSummary[];
  labels: (string[] | string)[];
  series: IChartSeries[];
}

export interface IChartSeries {
  name?: string;
  data: number[];
  label?: string[];
  slug?: string;
  color?: string;
  filters?: IReportsViewParams;
  extras?: {
    breakdownCustomElement?: React.ReactNode;
    customAggregate?: number;
    [key: string]: number[] | string[] | number | string | React.ReactNode | undefined;
  };
  csvColumnName?: string;
  csvColumnOrder?: number;
}

export interface ILast7DaysReportData {
  newTickets: number;
  newTicketsDifference: number;
  ticketRatingsGreat: number;
  ticketRatingsGreatDifference: number;
  ticketRatingsOkay: number;
  ticketRatingsOkayDifference: number;
  ticketRatingsBad: number;
  ticketRatingsBadDifference: number;
  solvedTickets: number;
  closedTickets: number;
  averageFirstResponseTime: number;
  chart: IChartData;
}

export interface IScheduledReportsData {
  data: IKeyAny<IScheduledReport>;
  sortedIds: string[];
  highlightedIds?: string[];
}

export interface IScheduledReport {
  id: string;
  format: string;
  licenceId: string;
  name: string;
  nextReport: string;
  recipients: string[];
  recurs: string;
  source: string;
  status: string;
}

export interface IReportCreatePayload {
  id?: string;
  email: string[];
  format: string;
  type: string;
  period?: string;
  encoding?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface IReportCreatePayloadData {
  data: IReportCreatePayload;
  reportType: ReportType;
}

export interface IScheduledReportEditData {
  data: IScheduledReportEdit;
  reportType: ReportType;
}

export interface IScheduledReportEdit {
  id: string;
  email: string[];
  format: string;
  encoding?: string;
  type: string;
  period: string;
  typeCurrent: string;
  periodCurrent: string;
}

export interface IScheduledReportDelete {
  id: string;
}

export interface IScheduledReportDeleteData {
  reportType: ReportType;
  data: IScheduledReportDelete;
}

export interface ILast7DaysReportChatsData {
  totalChats: number;
  totalChatsDifference: number;
  queuedVisitors: number | string;
  chatSatisfaction: string;
  chatSatisfactionDifference: number;
  chart: IChartData;
}

export interface ITotalChatsReportData {
  totalChats: IChartData;
}

export interface IChatEngagementReportData {
  chatEngagement: IChartData;
}

export interface IChatDurationData {
  chatDuration: IChartData;
}

export interface IChatResponseTimeData {
  firstResponseTime: IChartData;
  responseTime: IChartData;
}

export interface IChatAvailabilityData {
  chatAvailability: IChartData;
}

export interface IQueuedCustomersData {
  queuedCustomers: IChartData;
  waitingTime: IChartData;
}

export interface IGreetingsConversionData {
  greetingsConversion: IChartData;
}

export interface INewTicketsData {
  newTickets: IChartData;
  ticketSources: IChartData;
}

export interface IMissedChatsData {
  missedChats: IChartData;
}

export interface IChatRatingsData {
  ranking: IRanking[];
  chatRating: IChartData;
}

export interface ITicketRatingsData {
  ranking: IRanking[];
  ticketRating: IChartData;
  ticketRatingPerTeam: ITicketRatingsPerEntity[];
  ticketRatingPerAgent: ITicketRatingsPerEntity[];
}

export interface ISolvedClosedTicketsData {
  closedTickets: ITicketsStatusStats;
  solvedTickets: ITicketsStatusStats;
  closedTicketsPerAgent: ITicketsStatusStats;
  closedTicketsPerTeam: ITicketsStatusStats;
  solvedTicketsPerAgent: ITicketsStatusStats;
  solvedTicketsPerTeam: ITicketsStatusStats;
}
export interface ISolvedClosedTicketsStats {
  solvedClosedTickets: IChartData;
  solvedAndClosedTicketsPerTeam: ISolvedAndClosedStats[];
  solvedAndClosedTicketsPerAgent: ISolvedAndClosedStats[];
}

export interface ITicketFirstResponseTimeData {
  firstResponseTime: IChartData;
  firstResponseTimePerTeam: IFirstResponseTimePerEntity[];
  firstResponseTimePerAgent: IFirstResponseTimePerEntity[];
}

export interface IHandlingTimeByStatusData {
  handlingTimeByStatus: IChartData;
  handlingTimeByPerTeam: IHandlingTimeByStatusPerEntity[];
  handlingTimeByPerAgent: IHandlingTimeByStatusPerEntity[];
}

export interface ITicketResolutionTimeData {
  resolutionTime: IChartData;
  resolutionTimePerTeam: ITicketResolutionTimePerEntity[];
  resolutionTimePerAgent: ITicketResolutionTimePerEntity[];
}

export interface ITrackedSalesData {
  salesMade: IChartData;
  salesValue: IChartData;
}

export interface IStaffingPredictionData {
  staffingPrediction: IChartData;
}

export interface IAgentActivityData {
  events: KeyMap<IAgentActivityEvent[]>;
}

export enum AgentActivityEventType {
  Away = "status_away",
  Online = "online",
}

export interface IAgentActivityEvent {
  type: AgentActivityEventType;
  start: Date;
  end: Date;
}

export interface IChatSurveyQuestion {
  id: string;
  type: string;
  labels: string[];
  question: string;
  answersCount: number;
  answers: IChatSurveyAnswer[];
}

export interface IChatSurveyAnswer {
  id: number;
  name: string;
  count: number;
}

export interface IReportsViewSetQueryParamsFromURLPayload {
  params: IReportsViewParams;
}

export interface IReportsViewUpdateFilterPayload<T extends keyof IReportsViewFilters> {
  name: T;
  value: IFilters[T];
  compare?: IFilters[T];
}

export interface IReportsViewSetReportDistributionPayload {
  type: ReportDistribution;
}

export interface IRanking {
  id: string;
  photoUrl: string;
  email: string;
  name: string;
  removed: boolean;
  total: number;
  seeMoreUrl: string;
  seeMoreTitle: string;
  ratedGreatCount: number;
  ratedGreatPercentage: number;
  ratedOkayCount: number;
  ratedOkayPercentage: number;
  ratedBadCount: number;
  ratedBadPercentage: number;
  seeMoreRatedGreatUrl: string;
  seeMoreRatedGreatTitle: string;
  seeMoreRatedOkayUrl: string;
  seeMoreRatedOkayTitle: string;
  seeMoreRatedBadUrl: string;
  seeMoreRatedBadTitle: string;
}

export interface IQueueAbandonmentReportData {
  page: number;
  pages: number;
  total: number;
  withPrechat: number;
}

export interface IQueueAbandonmentPrechatQuestion {
  label: string;
  value: string;
}

export interface IHeatmapData {
  chartLabels: string[];
  chartSeries: IHeatmapSingleData[];
}

export interface IHeatmapSingleData {
  x: number;
  y: number;
  value: number;
  color?: string;
}

export interface IHeatmapOptions {
  series: {
    name: string;
    data: IHeatmapSingleData;
  };
  legend: {
    title: string;
    width?: number;
  };
  chart?: {
    height: string;
    marginTop?: string;
  };
  color?: {
    minColor: string;
    maxColor: string;
  };
  average?: {
    all: number;
    hour: number;
  };
}

export interface IReportDataSet {
  id: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: any;
  data: IChartData;
}

export enum ReportFetchFailureReason {
  TooManyTeams = "TOO_MUCH_TEAMS",
  TooManyAgents = "TOO_MUCH_AGENTS",
  TooManyTags = "TOO_MUCH_TAGS",
  DateRangeTooLong = "DATE_RANGE_TOO_LONG",
}

export interface INewTicketsSourceMeta {
  color: string;
}

export interface INewTicketsSeries {
  name: string;
  color: string;
  data: number[];
  label: string[];
}

export interface INewTicketsSummary {
  name: string;
  color: string;
  value: number;
  label: string;
}
